import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, MapPin } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Admin-only MAP view of parent-set drop-off pins (mirrored into BusDropoffPins).
// Plain Leaflet + free OpenStreetMap tiles (no API key / no billing). Each marker
// shows the bus number and a popup with family, student, parent, leg + address.
// Filter by text and by bus number.
const JOUNIEH = [33.9808, 35.6178];

const BusDropoffPinsModal = ({ isOpen, onClose, apiCall, showError }) => {
  const [loading, setLoading] = useState(true);
  const [pins, setPins] = useState([]);
  const [search, setSearch] = useState('');
  const [busFilter, setBusFilter] = useState('all');

  const mapRef = useRef(null);        // Leaflet map instance
  const layerRef = useRef(null);      // markers layer group
  const containerRef = useRef(null);  // the map div

  // Load pins when the modal opens.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await apiCall('/mobile/bus-dropoff-pins');
        if (!cancelled) setPins(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) showError('Failed to load drop-off pins: ' + error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const busOptions = useMemo(
    () => Array.from(new Set(pins.map((p) => p.BusNumber).filter((v) => v != null))).sort((a, b) => a - b),
    [pins]
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return pins.filter((p) => {
      if (busFilter !== 'all' && String(p.BusNumber) !== String(busFilter)) return false;
      if (!term) return true;
      return [p.FamilyID, p.StudentName, p.ParentName, p.BusNumber]
        .some((v) => String(v || '').toLowerCase().includes(term));
    });
  }, [pins, search, busFilter]);

  // Create the map once the modal is open and the container exists.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => {
      if (!containerRef.current || mapRef.current) return;
      const map = L.map(containerRef.current).setView(JOUNIEH, 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);
      layerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 50);
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen]);

  // Tear the map down when closed so it re-inits cleanly next open.
  useEffect(() => {
    if (isOpen) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      layerRef.current = null;
    }
  }, [isOpen]);

  // Redraw markers whenever the filtered set changes.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const latlngs = [];
    rows.forEach((p) => {
      if (p.Latitude == null || p.Longitude == null) return;
      const ll = [Number(p.Latitude), Number(p.Longitude)];
      latlngs.push(ll);
      const busLabel = p.BusNumber != null ? `#${p.BusNumber}` : '?';
      const icon = L.divIcon({
        className: '',
        html: `<div style="background:#fff;color:#000;border-radius:9999px;padding:4px 11px;font-size:16px;font-weight:800;line-height:1;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.55);border:2px solid #4f46e5;transform:translate(-50%,-50%)">🚌&nbsp;${busLabel}</div>`,
        iconSize: [0, 0],
      });
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
      const popup = `
        <div style="font-size:13px;line-height:1.5">
          <b>Family ${esc(p.FamilyID)}</b><br/>
          ${esc(p.StudentName) || esc(p.StudentID)}<br/>
          <span style="color:#555">${esc(p.ParentName) || ''}</span><br/>
          Bus ${busLabel} · ${p.Leg === 'evening' ? '🌙 Evening' : '🌅 Morning'}<br/>
          ${p.AddressText ? `<span style="color:#555">${esc(p.AddressText)}</span>` : ''}
        </div>`;
      L.marker(ll, { icon }).bindPopup(popup).addTo(layer);
    });
    if (latlngs.length) map.fitBounds(latlngs, { padding: [50, 50], maxZoom: 15 });
    setTimeout(() => map.invalidateSize(), 30);
  }, [rows]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-indigo-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Drop-off Pins</h2>
              <p className="text-sm text-gray-500">{rows.length} of {pins.length} shown</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center p-4 border-b bg-gray-50">
          <input
            type="text"
            placeholder="🔍 Search family, student, parent, bus #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[220px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
          <select
            value={busFilter}
            onChange={(e) => setBusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All buses</option>
            {busOptions.map((b) => (
              <option key={b} value={b}>Bus #{b}</option>
            ))}
          </select>
        </div>

        {/* Map */}
        <div className="relative flex-1">
          <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-[500]">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          ) : !loading && pins.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500 z-[500] pointer-events-none">
              No drop-off pins yet — parents set these in the app, then run “Pull Drop-off Pins”.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BusDropoffPinsModal;
